import type { ReactNode } from 'react';
import { Draggable, Droppable, type DraggableProvidedDragHandleProps } from '@hello-pangea/dnd';
import WidgetContainer from '@/components/dashboard/WidgetContainer';
import { useTranslation } from '@/store/languageStore';
import {
  COL_SPANS,
  ROW_SPANS,
  type ColSpan,
  type DashboardGridWidget,
  type RowSpan,
} from '@/utils/dashboardGrid';
import '@/styles/dashboardGrid.css';

interface DashboardWidgetGridProps {
  widgets: DashboardGridWidget[];
  editMode: boolean;
  renderBody: (widget: DashboardGridWidget) => ReactNode;
  onRemove: (widgetId: string) => void;
  onResize: (widgetId: string, size: { colSpan?: ColSpan; rowSpan?: RowSpan }) => void;
}

const WIDTH_KEY: Record<ColSpan, string> = {
  12: 'dashboard.widthFull',
  8: 'dashboard.widthWide',
  6: 'dashboard.widthHalf',
  4: 'dashboard.widthThird',
};

const HEIGHT_KEY: Record<RowSpan, string> = {
  2: 'dashboard.heightCompact',
  3: 'dashboard.heightShort',
  4: 'dashboard.heightMedium',
  5: 'dashboard.heightTall',
  6: 'dashboard.heightLarge',
  7: 'dashboard.heightXLarge',
};

function SizeSelect<T extends number>({
  label,
  value,
  options,
  labelFor,
  onChange,
}: {
  label: string;
  value: T;
  options: readonly T[];
  labelFor: (option: T) => string;
  onChange: (option: T) => void;
}) {
  return (
    <label className="flex items-center gap-1 text-xs text-blue-700 dark:text-blue-300">
      <span className="sr-only">{label}</span>
      <select
        aria-label={label}
        value={value}
        onChange={(event) => onChange(Number(event.target.value) as T)}
        className="bg-bgElevated border border-blue-200 dark:border-blue-800 rounded px-1.5 py-0.5 text-xs text-textDefault"
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {labelFor(option)}
          </option>
        ))}
      </select>
    </label>
  );
}

function GridItem({
  widget,
  editMode,
  dragHandleProps,
  renderBody,
  onRemove,
  onResize,
}: {
  widget: DashboardGridWidget;
  editMode: boolean;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
  renderBody: (widget: DashboardGridWidget) => ReactNode;
  onRemove: (widgetId: string) => void;
  onResize: (widgetId: string, size: { colSpan?: ColSpan; rowSpan?: RowSpan }) => void;
}) {
  const { t } = useTranslation();
  const colSpan = (widget.colSpan ?? 4) as ColSpan;
  const rowSpan = (widget.rowSpan ?? 4) as RowSpan;
  const rowOptions = widget.widgetType === 'production-cost'
    ? ROW_SPANS.filter((span) => span !== 7)
    : ROW_SPANS;

  return (
    <WidgetContainer
      fill
      bodyScroll={widget.widgetType !== 'my-bookings' && widget.widgetType !== 'vehicle-count' && widget.widgetType !== 'action-required' && widget.widgetType !== 'production-cost' && widget.widgetType !== 'mine' && widget.widgetType !== 'service-cost'}
      editMode={editMode}
      onRemove={editMode ? () => onRemove(widget.id) : undefined}
      dragHandleProps={dragHandleProps ?? undefined}
      toolbar={
        editMode ? (
          <div className="flex items-center gap-2">
            <SizeSelect
              label={t('dashboard.widgetWidth')}
              value={colSpan}
              options={COL_SPANS}
              labelFor={(option) => t(WIDTH_KEY[option])}
              onChange={(next) => onResize(widget.id, { colSpan: next })}
            />
            <SizeSelect
              label={t('dashboard.widgetHeight')}
              value={rowSpan}
              options={rowOptions}
              labelFor={(option) => t(HEIGHT_KEY[option])}
              onChange={(next) => onResize(widget.id, { rowSpan: next })}
            />
          </div>
        ) : undefined
      }
    >
      {renderBody(widget)}
    </WidgetContainer>
  );
}

function DashboardWidgetGrid({
  widgets,
  editMode,
  renderBody,
  onRemove,
  onResize,
}: DashboardWidgetGridProps) {
  const item = (widget: DashboardGridWidget, dragHandleProps?: DraggableProvidedDragHandleProps | null) => (
    <GridItem
      widget={widget}
      editMode={editMode}
      dragHandleProps={dragHandleProps}
      renderBody={renderBody}
      onRemove={onRemove}
      onResize={onResize}
    />
  );

  if (!editMode) {
    return (
      <div className="dq-widget-grid">
        {widgets.map((widget) => (
          <div
            key={widget.id}
            className="dq-grid-item"
            data-cols={widget.colSpan}
            data-rows={widget.rowSpan}
            data-widget={widget.widgetType}
          >
            {item(widget)}
          </div>
        ))}
      </div>
    );
  }

  return (
    <Droppable droppableId="dashboard-grid">
      {(provided) => (
        <div ref={provided.innerRef} {...provided.droppableProps} className="dq-widget-grid">
          {widgets.map((widget, index) => (
            <Draggable key={widget.id} draggableId={widget.id} index={index}>
              {(drag, snapshot) => (
                <div
                  ref={drag.innerRef}
                  {...drag.draggableProps}
                  className={`dq-grid-item ${snapshot.isDragging ? 'z-50 shadow-2xl' : ''}`}
                  data-cols={widget.colSpan}
                  data-rows={widget.rowSpan}
                  data-widget={widget.widgetType}
                >
                  {item(widget, drag.dragHandleProps)}
                </div>
              )}
            </Draggable>
          ))}
          {provided.placeholder}
        </div>
      )}
    </Droppable>
  );
}

export default DashboardWidgetGrid;
